import {
	CpuAimProfile,
	CpuDifficulty,
	Inventory,
	ScorchedEarthConfig,
	WeaponDef,
	WeaponId,
} from './scorched-earth.types';

export const WORLD_WIDTH = 720;
export const WORLD_HEIGHT = 400;
export const SKY_HEIGHT = 320;
export const TANK_WIDTH = 22;
export const TANK_HEIGHT = 12;
export const MIN_POWER = 10;
export const MAX_POWER = 100;
export const MIN_ANGLE = 0;
export const MAX_ANGLE = 180;
export const GRAVITY = 0.08;
export const MAX_WIND = 40;
export const PROJECTILE_RADIUS = 2;
export const TICK_MS = 16;
export const MAX_PROJECTILE_AGE = 900;

export const PLAYER_COLORS = ['#00ffcc', '#ff6b6b', '#ffd166', '#7b5cff'];
export const DEFAULT_PLAYER_NAMES = ['Alpha', 'Bravo', 'Charlie', 'Delta'];
export const DEFAULT_BEST_OF = 3;
export const TURN_TIMER_TICKS = 600;

export const CPU_AIM_PROFILES: Record<CpuDifficulty, CpuAimProfile> = {
	easy: { angleErrorDeg: 18, powerError: 22, weaponPickNoise: 0.35 },
	normal: { angleErrorDeg: 8, powerError: 12, weaponPickNoise: 0.15 },
	hard: { angleErrorDeg: 3, powerError: 5, weaponPickNoise: 0.05 },
};

export const SCORCHED_EARTH_CONFIG: ScorchedEarthConfig = {
	worldWidth: WORLD_WIDTH,
	worldHeight: WORLD_HEIGHT,
	gravity: GRAVITY,
	maxWind: MAX_WIND,
	minPlayers: 2,
	maxPlayers: 4,
	roundShopCredits: 120,
	roundWinCredits: 80,
	killBonusCredits: 25,
	defaultBestOf: DEFAULT_BEST_OF,
	turnTimerTicks: TURN_TIMER_TICKS,
};

export const WEAPON_ORDER: WeaponId[] = [
	'babyMissile',
	'missile',
	'babyNuke',
	'nuke',
	'mirv',
	'napalm',
];

export const WEAPONS: Record<WeaponId, WeaponDef> = {
	babyMissile: {
		id: 'babyMissile',
		label: 'Baby Missile',
		shortLabel: 'B.MSL',
		description: 'Cheap skirmish round — chip damage, terrain probes, and finishing wounded tanks.',
		blastRadius: 16,
		maxDamage: 18,
		shopCost: 8,
		startingAmmo: 12,
		projectileSpeed: 0.075,
		gravityScale: 1,
		windScale: 1,
	},
	missile: {
		id: 'missile',
		label: 'Missile',
		shortLabel: 'MSL',
		description: 'Reliable workhorse — balanced blast radius and damage for direct trades.',
		blastRadius: 28,
		maxDamage: 32,
		shopCost: 14,
		startingAmmo: 8,
		projectileSpeed: 0.08,
		gravityScale: 1,
		windScale: 1,
	},
	babyNuke: {
		id: 'babyNuke',
		label: 'Baby Nuke',
		shortLabel: 'B.NUK',
		description: 'Area denial on a budget — reshapes cover and punishes clustered foes.',
		blastRadius: 42,
		maxDamage: 45,
		shopCost: 28,
		startingAmmo: 3,
		projectileSpeed: 0.065,
		gravityScale: 0.95,
		windScale: 0.85,
	},
	nuke: {
		id: 'nuke',
		label: 'Nuke',
		shortLabel: 'NUK',
		description: 'Maximum destruction — massive craters and lethal splash for decisive strikes.',
		blastRadius: 68,
		maxDamage: 75,
		shopCost: 55,
		startingAmmo: 2,
		projectileSpeed: 0.06,
		gravityScale: 0.9,
		windScale: 0.75,
	},
	mirv: {
		id: 'mirv',
		label: 'MIRV',
		shortLabel: 'MIRV',
		description: 'Saturates a zone with split warheads — strong against spread-out or uncertain targets.',
		blastRadius: 22,
		maxDamage: 26,
		shopCost: 40,
		startingAmmo: 2,
		projectileSpeed: 0.075,
		gravityScale: 1,
		windScale: 1,
		splits: 5,
		splitSpreadDeg: 18,
	},
	napalm: {
		id: 'napalm',
		label: 'Napalm',
		shortLabel: 'NAP',
		description: 'Lingering burn pool — drains health over time and controls low ground.',
		blastRadius: 12,
		maxDamage: 10,
		shopCost: 22,
		startingAmmo: 3,
		projectileSpeed: 0.065,
		gravityScale: 1.05,
		windScale: 0.6,
		napalmRadius: 34,
		napalmDurationTicks: 180,
		napalmTickDamage: 3,
	},
};

export function createDefaultInventory(): Inventory {
	return {
		babyMissile: WEAPONS.babyMissile.startingAmmo,
		missile: WEAPONS.missile.startingAmmo,
		babyNuke: WEAPONS.babyNuke.startingAmmo,
		nuke: WEAPONS.nuke.startingAmmo,
		mirv: WEAPONS.mirv.startingAmmo,
		napalm: WEAPONS.napalm.startingAmmo,
	};
}

export function cloneInventory(inv: Inventory): Inventory {
	return { ...inv };
}
